import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../shared/helpers/plural';
import { useLocation } from '../../../contexts/RouterContext';
import { useIsQualityEnabled } from '../../../helpers/features/quality';
import Badge from '../../generic/Badge/Badge';
import Button from '../../generic/Button/Button';
import FallbackImage from '../../generic/FallbackImage';
import Checkbox from '../../generic/Forms/Checkbox';
import List from '../../generic/List';
import type { PackageDetails } from '../../../../shared/organizationPackages/response';

import PackageDetailsTableRow from './PackageDetailsTableRow';

type Props = {
  packageLocator?: string;
  isLoading: boolean;
  packageDetails: PackageDetails[] | undefined;
  onShowBlockModal: () => void;
  onToggleAllVersions: () => void;
  onToggleVersion: (packageRevision: string) => void;
  selectedVersionLocators: Set<string>;
  versionQueryString: string;
};

type PackageDetailsTableBodyProps = Omit<Props, 'onShowBlockModal' | 'onToggleAllVersions'> & {
  isLoading: boolean;
  packageDetails: PackageDetails[] | undefined;
};

/**
 * Inner body part of the table.
 */
const PackageDetailsTableBody: VoidFunctionComponent<PackageDetailsTableBodyProps> = ({
  packageLocator,
  isLoading,
  packageDetails,
  onToggleVersion,
  selectedVersionLocators,
  versionQueryString,
}) => {
  const location = useLocation();

  if (isLoading || !packageLocator) {
    return <>{_.times(3, () => <List.RowLoading />)}</>;
  }

  if (!packageDetails || packageDetails?.length === 0) {
    return (
      <FallbackImage src="no-results" alt="No versions found!" hasPadding>
        No versions found!
      </FallbackImage>
    );
  }

  return (
    <>
      {packageDetails.map((details) => (
        <PackageDetailsTableRow
          key={details.packageRevision}
          pathname={location.pathname}
          isSelected={selectedVersionLocators.has(details.packageRevision)}
          onToggleVersion={onToggleVersion}
          versionQueryString={versionQueryString}
          {...details}
        />
      ))}
    </>
  );
};

/**
 * Renders the list of versions for a given package.
 *
 * If the package denial workflow is enabled, then this will also
 * display checkboxes next to each version, allowing the user
 * to select specific versions of this package to deny.
 */
const PackageDetailsTable: VoidFunctionComponent<Props> = ({
  packageLocator,
  isLoading,
  packageDetails,
  onShowBlockModal,
  onToggleAllVersions,
  onToggleVersion,
  selectedVersionLocators,
  versionQueryString,
}) => {
  const selectedVersionsCount = selectedVersionLocators.size;
  const areSomeVersionsSelected = selectedVersionsCount > 0;
  const areAllVersionsSelected = Boolean(packageDetails?.length)
    && selectedVersionsCount === packageDetails?.length;

  const hasQuality = useIsQualityEnabled();

  return (
    <div className="package-inventory__details__table">
      <List className="package-inventory__details__table__list">
        <List.Header hasCheckbox={hasQuality}>
          <div className="package-inventory__details__table__list__header__selection">
            {hasQuality && (
              <Checkbox
                isChecked={areAllVersionsSelected}
                isIndeterminate={!areAllVersionsSelected && areSomeVersionsSelected}
                onChange={onToggleAllVersions}
                isDisabled={isLoading}
                isRippleDisabled
              />
            )}
            {areSomeVersionsSelected ? (
              <Button
                type="secondary"
                size="small"
                onClick={onShowBlockModal}
              >
                Block {plural(selectedVersionsCount, 'version')}
              </Button>
            ) : (
              <div className="package-inventory__details__table__list__header__title">
                <List.HeaderText>Versions</List.HeaderText>
                <span className="inline-block margin-left-xs">
                  <Badge
                    shape="circle"
                    color="light-gray"
                  >
                    {isLoading || !packageDetails ? '...' : packageDetails.length.toString()}
                  </Badge>
                </span>
              </div>
            )}
          </div>
        </List.Header>
        <List.Body>
          <PackageDetailsTableBody
            packageLocator={packageLocator}
            isLoading={isLoading}
            packageDetails={packageDetails}
            onToggleVersion={onToggleVersion}
            selectedVersionLocators={selectedVersionLocators}
            versionQueryString={versionQueryString}
          />
        </List.Body>
      </List>
    </div>
  );
};

export default PackageDetailsTable;
